import {
  FULL_CROP,
  MIN_MASK_SIZE,
  clamp,
  croppedItemBox,
  dpiLevel,
  sameAspect,
  type PrintDpi,
  type CropDesignItem,
  type NormalizedCropRect,
} from './cropHelpers';
import { t } from './i18n';

export type { NormalizedCropRect };

export interface CropModalOptions {
  /**
   * Preview URLs, smallest usable first. The crop is stored normalized, so a medium rendition
   * crops exactly like the original and decodes far faster; a candidate that fails to load or
   * whose aspect differs from the source (a hard-cropped rendition) falls through to the next.
   */
  sources: string[];
  sourceW: number;
  sourceH: number;
  /** SVG decodes at an arbitrary default size, so its aspect cannot be checked against the source. */
  vector?: boolean;
  item: CropDesignItem;
  initialCrop?: NormalizedCropRect | null;
  /** Print resolution of the item as it would be committed; null hides the quality line. */
  printDpi?: (candidate: CropDesignItem) => PrintDpi | null;
  onCommit: (patch: Record<string, unknown>) => void;
  onCancel: () => void;
}

type Preset = 'original' | 'free' | '1:1' | '4:3' | '3:4' | '16:9';

const PRESETS: { id: Preset; label: string; ratio: number | null }[] = [
  { id: 'original', label: 'Original', ratio: null },
  { id: 'free', label: 'Free', ratio: null },
  { id: '1:1', label: '1:1', ratio: 1 },
  { id: '4:3', label: '4:3', ratio: 4 / 3 },
  { id: '3:4', label: '3:4', ratio: 3 / 4 },
  { id: '16:9', label: '16:9', ratio: 16 / 9 },
];

const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const WHEEL_ZOOM_SPEED = 0.0015;
const HANDLE_DIRECTIONS = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as const;
type Direction = (typeof HANDLE_DIRECTIONS)[number];

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.min(1, Math.max(0, v));
}

function isMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
}

/**
 * Open a Canva-style crop modal. Returns a teardown function.
 *
 * The editor shows the full image with the area outside the crop frame dimmed,
 * lets the user pan/zoom the image and resize the crop frame (with optional
 * aspect lock), then commits a normalized `imageTransform.crop` rect plus a
 * resized item box that matches the crop's pixel aspect ratio.
 */
export function openCropModal(opts: CropModalOptions): () => void {
  const mobile = isMobileViewport();

  // --- DOM scaffold -------------------------------------------------------
  const previousFocus = document.activeElement as HTMLElement | null;
  const overlay = document.createElement('div');
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', t('Crop image'));
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '999999',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(9,9,11,0.72)',
    backdropFilter: 'blur(2px)',
    padding: mobile ? '0' : '24px',
    boxSizing: 'border-box',
  } as CSSStyleDeclaration);

  const panel = document.createElement('div');
  Object.assign(panel.style, {
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--vs-surface, #18181b)',
    color: 'var(--vs-ink, #fff)',
    border: '1px solid var(--vs-line, rgba(255,255,255,0.08))',
    overflow: 'hidden',
    boxShadow: '0 24px 70px rgba(0,0,0,0.55)',
    width: mobile ? '100%' : 'min(92vw, 920px)',
    height: mobile ? '100%' : 'min(88vh, 760px)',
    borderRadius: mobile ? '0' : '18px',
  } as CSSStyleDeclaration);
  overlay.appendChild(panel);

  // Header
  const header = document.createElement('div');
  Object.assign(header.style, {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '12px 16px',
    paddingTop: mobile ? 'max(12px, env(safe-area-inset-top))' : '12px',
    background: 'var(--vs-surface, #18181b)',
    borderBottom: '1px solid var(--vs-line, rgba(255,255,255,0.08))',
    flexShrink: '0',
  } as CSSStyleDeclaration);

  const cancelBtn = chromeButton(t('Cancel'), 'ghost');
  const title = document.createElement('span');
  title.textContent = t('Crop image');
  Object.assign(title.style, { fontSize: '14px', fontWeight: '600', color: 'var(--vs-ink, #fff)' } as CSSStyleDeclaration);
  const doneBtn = chromeButton(t('Done'), 'primary');
  header.append(cancelBtn, title, doneBtn);

  // Stage
  const stage = document.createElement('div');
  Object.assign(stage.style, {
    position: 'relative',
    flex: '1',
    overflow: 'hidden',
    backgroundColor: 'var(--vs-bg, #0a0a0a)',
    backgroundImage: 'radial-gradient(var(--vs-dot, rgba(255,255,255,0.1)) 1px, transparent 1px)',
    backgroundSize: '20px 20px',
    touchAction: 'none',
    userSelect: 'none',
    cursor: 'grab',
  } as CSSStyleDeclaration);

  const img = document.createElement('img');
  img.alt = '';
  img.draggable = false;
  img.decoding = 'async';
  Object.assign(img.style, {
    position: 'absolute',
    left: '0',
    visibility: 'hidden',
    // Light checker behind the artwork so black / transparent PNG & SVG stays visible on a dark stage.
    backgroundColor: '#f2f2f7',
    backgroundImage: 'conic-gradient(#dcdce1 25%, transparent 0 50%, #dcdce1 0 75%, transparent 0)',
    backgroundSize: '16px 16px',
    top: '0',
    width: '0',
    height: '0',
    maxWidth: 'none',
    userSelect: 'none',
    pointerEvents: 'none',
    transformOrigin: '0 0',
    willChange: 'transform',
  } as CSSStyleDeclaration);
  stage.appendChild(img);

  // Loading / error message, centered on the stage until the picture is ready.
  const status = document.createElement('div');
  status.textContent = t('Loading…');
  Object.assign(status.style, {
    position: 'absolute', inset: '0', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', color: 'var(--vs-ink-2, rgba(255,255,255,0.75))', pointerEvents: 'none',
  } as CSSStyleDeclaration);
  stage.appendChild(status);

  const frame = document.createElement('div');
  Object.assign(frame.style, {
    position: 'absolute',
    left: '0',
    top: '0',
    width: '0',
    height: '0',
    boxSizing: 'border-box',
    border: '2px solid #fff',
    boxShadow: '0 0 0 1px rgba(0,0,0,0.6), 0 0 0 9999px rgba(0,0,0,0.6)',
    cursor: 'move',
    touchAction: 'none',
    visibility: 'hidden',
    willChange: 'transform',
  } as CSSStyleDeclaration);
  stage.appendChild(frame);

  // Rule-of-thirds grid
  for (const pos of ['33.333%', '66.666%'] as const) {
    const vLine = document.createElement('div');
    Object.assign(vLine.style, {
      position: 'absolute', top: '0', bottom: '0', left: pos, width: '1px',
      background: 'rgba(255,255,255,0.35)', pointerEvents: 'none',
    } as CSSStyleDeclaration);
    const hLine = document.createElement('div');
    Object.assign(hLine.style, {
      position: 'absolute', left: '0', right: '0', top: pos, height: '1px',
      background: 'rgba(255,255,255,0.35)', pointerEvents: 'none',
    } as CSSStyleDeclaration);
    frame.append(vLine, hLine);
  }

  // Handles
  const handleEls: Record<Direction, HTMLElement> = {} as Record<Direction, HTMLElement>;
  const hs = mobile ? 22 : 14;
  const half = hs / 2;
  for (const dir of HANDLE_DIRECTIONS) {
    const handle = document.createElement('span');
    Object.assign(handle.style, {
      position: 'absolute',
      width: `${hs}px`,
      height: `${hs}px`,
      borderRadius: '999px',
      background: '#fff',
      border: '1px solid var(--vs-accent, #007aff)',
      boxShadow: '0 1px 6px rgba(0,0,0,0.4)',
      touchAction: 'none',
      zIndex: '3',
      cursor: cursorFor(dir),
    } as CSSStyleDeclaration);
    if (dir.includes('n')) handle.style.top = `-${half}px`;
    if (dir.includes('s')) handle.style.bottom = `-${half}px`;
    if (dir.includes('w')) handle.style.left = `-${half}px`;
    if (dir.includes('e')) handle.style.right = `-${half}px`;
    if (dir === 'n' || dir === 's') { handle.style.left = '50%'; handle.style.transform = 'translateX(-50%)'; }
    if (dir === 'e' || dir === 'w') { handle.style.top = '50%'; handle.style.transform = 'translateY(-50%)'; }
    handleEls[dir] = handle;
    frame.appendChild(handle);
  }

  // Footer
  const footer = document.createElement('div');
  Object.assign(footer.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '12px 16px',
    paddingBottom: mobile ? 'max(12px, env(safe-area-inset-bottom))' : '14px',
    background: 'var(--vs-surface, #18181b)',
    borderTop: '1px solid var(--vs-line, rgba(255,255,255,0.08))',
    flexShrink: '0',
  } as CSSStyleDeclaration);

  const presetRow = document.createElement('div');
  Object.assign(presetRow.style, {
    display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', justifyContent: 'center',
  } as CSSStyleDeclaration);
  const presetButtons: Record<Preset, HTMLButtonElement> = {} as Record<Preset, HTMLButtonElement>;
  for (const preset of PRESETS) {
    const btn = presetButton(t(preset.label));
    btn.addEventListener('click', () => selectPreset(preset.id));
    presetButtons[preset.id] = btn;
    presetRow.appendChild(btn);
  }

  const zoomRow = document.createElement('div');
  Object.assign(zoomRow.style, {
    display: 'flex', alignItems: 'center', gap: '10px',
  } as CSSStyleDeclaration);
  const zoomLabel = document.createElement('span');
  zoomLabel.textContent = t('Zoom');
  Object.assign(zoomLabel.style, { fontSize: '12px', color: 'var(--vs-ink-2, rgba(255,255,255,0.65))', flexShrink: '0' } as CSSStyleDeclaration);
  const zoomSlider = document.createElement('input');
  zoomSlider.type = 'range';
  zoomSlider.min = String(ZOOM_MIN);
  zoomSlider.max = String(ZOOM_MAX);
  zoomSlider.step = '0.01';
  zoomSlider.value = '1';
  Object.assign(zoomSlider.style, { flex: '1', accentColor: 'var(--vs-accent, #007aff)', cursor: 'pointer' } as CSSStyleDeclaration);
  const resetBtn = chromeButton(t('Reset'), 'ghost');
  resetBtn.style.flexShrink = '0';
  zoomRow.append(zoomLabel, zoomSlider, resetBtn);

  // Live print quality of the crop as committed (only when a provider such as Preflight answers).
  const dpiLine = document.createElement('div');
  dpiLine.setAttribute('aria-live', 'polite');
  Object.assign(dpiLine.style, {
    display: 'none', alignItems: 'center', justifyContent: 'center', gap: '6px',
    fontSize: '12px', fontWeight: '600',
  } as CSSStyleDeclaration);

  footer.append(presetRow, zoomRow, dpiLine);
  panel.append(header, stage, footer);

  // --- State --------------------------------------------------------------
  let natW = Math.max(1, Number(opts.sourceW) || 0);
  let natH = Math.max(1, Number(opts.sourceH) || 0);
  let baseW = 0;
  let baseH = 0;
  let zoom = 1;
  let imgX = 0;
  let imgY = 0;
  // frame in stage px
  let fx = 0;
  let fy = 0;
  let fw = 0;
  let fh = 0;
  let activePreset: Preset = 'free';
  let ready = false;
  doneBtn.disabled = true;
  doneBtn.style.opacity = '0.5';

  const dispW = () => baseW * zoom;
  const dispH = () => baseH * zoom;

  function stageRect(): { w: number; h: number } {
    return { w: stage.clientWidth || 1, h: stage.clientHeight || 1 };
  }

  function computeBaseFit(): void {
    const { w, h } = stageRect();
    const pad = 0.88;
    const scale = Math.min((w * pad) / natW, (h * pad) / natH);
    baseW = Math.max(1, natW * scale);
    baseH = Math.max(1, natH * scale);
  }

  function centerImage(): void {
    const { w, h } = stageRect();
    imgX = (w - dispW()) / 2;
    imgY = (h - dispH()) / 2;
  }

  // Positions go through `transform` (compositor only); sizes are written only when they change,
  // so a pan or frame move never triggers layout.
  let shownImgW = -1;
  let shownImgH = -1;
  let shownFrameW = -1;
  let shownFrameH = -1;
  function renderPositions(): void {
    const dw = dispW();
    const dh = dispH();
    if (dw !== shownImgW || dh !== shownImgH) {
      img.style.width = `${dw}px`;
      img.style.height = `${dh}px`;
      shownImgW = dw;
      shownImgH = dh;
    }
    img.style.transform = `translate3d(${imgX}px, ${imgY}px, 0)`;
    if (fw !== shownFrameW || fh !== shownFrameH) {
      frame.style.width = `${fw}px`;
      frame.style.height = `${fh}px`;
      shownFrameW = fw;
      shownFrameH = fh;
    }
    frame.style.transform = `translate3d(${fx}px, ${fy}px, 0)`;
    renderDpi();
  }

  let shownDpi = '';
  function renderDpi(): void {
    if (!opts.printDpi || !ready) return;
    const dpi = opts.printDpi({ ...opts.item, ...buildPatch() } as CropDesignItem);
    const text = dpi ? dpiText(dpi) : '';
    if (text === shownDpi) return;
    shownDpi = text;
    dpiLine.style.display = text ? 'flex' : 'none';
    if (!dpi) return;
    const level = dpiLevel(dpi);
    const color = level === 'ok' ? 'var(--vs-ok, #34c759)' : level === 'warn' ? 'var(--vs-warn, #ff9f0a)' : 'var(--vs-danger, #ff453a)';
    dpiLine.style.color = color;
    const dot = document.createElement('span');
    Object.assign(dot.style, { width: '8px', height: '8px', borderRadius: '999px', background: color, flexShrink: '0' } as CSSStyleDeclaration);
    dpiLine.replaceChildren(dot, document.createTextNode(text));
  }

  let frameRequest = 0;
  function scheduleRender(): void {
    if (frameRequest) return;
    frameRequest = requestAnimationFrame(() => {
      frameRequest = 0;
      renderPositions();
    });
  }

  function syncPresetButtons(): void {
    for (const preset of PRESETS) {
      stylePresetButton(presetButtons[preset.id], preset.id === activePreset);
    }
  }

  /** Keep the image covering the frame (image cannot move past the frame edges). */
  function clampImageToFrame(): void {
    const dw = dispW();
    const dh = dispH();
    if (dw >= fw) imgX = clamp(imgX, fx + fw - dw, fx);
    else imgX = fx + (fw - dw) / 2;
    if (dh >= fh) imgY = clamp(imgY, fy + fh - dh, fy);
    else imgY = fy + (fh - dh) / 2;
  }

  /** Keep the frame inside the image rect. */
  function clampFrameToImage(): void {
    const dw = dispW();
    const dh = dispH();
    fw = Math.min(fw, dw);
    fh = Math.min(fh, dh);
    fx = clamp(fx, imgX, imgX + dw - fw);
    fy = clamp(fy, imgY, imgY + dh - fh);
  }

  function fitFrameWithAspect(ratio: number | null): void {
    const dw = dispW();
    const dh = dispH();
    if (ratio == null) {
      // Free / original -> use the full image rect as the starting frame.
      fw = dw;
      fh = dh;
    } else {
      let w = dw;
      let h = w / ratio;
      if (h > dh) { h = dh; w = h * ratio; }
      fw = w;
      fh = h;
    }
    // Align frame center to image center, then clamp inside image.
    fx = imgX + (dw - fw) / 2;
    fy = imgY + (dh - fh) / 2;
    clampFrameToImage();
  }

  function selectPreset(id: Preset): void {
    if (!ready) return;
    activePreset = id;
    if (id === 'original') {
      zoom = 1;
      zoomSlider.value = '1';
      centerImage();
      fitFrameWithAspect(null);
    } else {
      const ratio = PRESETS.find((p) => p.id === id)?.ratio ?? null;
      fitFrameWithAspect(ratio);
    }
    syncPresetButtons();
    renderPositions();
  }

  function aspectLock(): number | null {
    return PRESETS.find((p) => p.id === activePreset)?.ratio ?? null;
  }

  // --- Gestures -----------------------------------------------------------
  type Drag =
    | { kind: 'moveFrame'; startX: number; startY: number; fx: number; fy: number }
    | { kind: 'panImage'; startX: number; startY: number; imgX: number; imgY: number }
    | { kind: 'resize'; dir: Direction; startX: number; startY: number; fx: number; fy: number; fw: number; fh: number };
  let drag: Drag | null = null;
  let captureEl: HTMLElement | null = null;
  // Touch points on the stage, for pinch zoom.
  const touches = new Map<number, { x: number; y: number }>();
  let pinch: { distance: number; zoom: number } | null = null;

  function touchSpread(): { distance: number; cx: number; cy: number } {
    const [a, b] = [...touches.values()];
    return { distance: Math.hypot(a.x - b.x, a.y - b.y), cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2 };
  }

  // Drag inside the crop box -> move the box over the image.
  frame.addEventListener('pointerdown', (event) => {
    if ((event.target as HTMLElement) !== frame) return; // handles handled separately
    if (!ready || pinch) return;
    event.preventDefault();
    event.stopPropagation();
    captureEl = frame;
    frame.setPointerCapture(event.pointerId);
    drag = { kind: 'moveFrame', startX: event.clientX, startY: event.clientY, fx, fy };
  });

  // Drag on the dimmed area outside the box -> pan the image.
  // Second finger anywhere on the stage (frame included) turns the gesture into a pinch.
  stage.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'touch') return;
    touches.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (touches.size === 2 && ready) {
      drag = null;
      pinch = { distance: touchSpread().distance, zoom };
    }
  }, true);

  stage.addEventListener('pointerdown', (event) => {
    if ((event.target as HTMLElement) !== stage) return; // ignore frame/handles
    if (!ready || pinch) return;
    event.preventDefault();
    captureEl = stage;
    stage.setPointerCapture(event.pointerId);
    drag = { kind: 'panImage', startX: event.clientX, startY: event.clientY, imgX, imgY };
  });

  for (const dir of HANDLE_DIRECTIONS) {
    handleEls[dir].addEventListener('pointerdown', (event) => {
      if (!ready || pinch) return;
      event.preventDefault();
      event.stopPropagation();
      captureEl = handleEls[dir];
      handleEls[dir].setPointerCapture(event.pointerId);
      drag = { kind: 'resize', dir, startX: event.clientX, startY: event.clientY, fx, fy, fw, fh };
    });
  }

  function onPointerMove(event: PointerEvent): void {
    if (touches.has(event.pointerId)) {
      touches.set(event.pointerId, { x: event.clientX, y: event.clientY });
    }
    if (pinch && touches.size === 2) {
      event.preventDefault();
      const { distance, cx, cy } = touchSpread();
      const box = stage.getBoundingClientRect();
      setZoom(pinch.zoom * (distance / Math.max(1, pinch.distance)), cx - box.left, cy - box.top);
      return;
    }
    if (!drag) return;
    event.preventDefault();
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;

    if (drag.kind === 'moveFrame') {
      fx = clamp(drag.fx + dx, imgX, imgX + dispW() - fw);
      fy = clamp(drag.fy + dy, imgY, imgY + dispH() - fh);
    } else if (drag.kind === 'panImage') {
      imgX = drag.imgX + dx;
      imgY = drag.imgY + dy;
      clampImageToFrame();
    } else {
      resizeFrame(drag, dx, dy);
    }
    scheduleRender();
  }

  function resizeFrame(d: Extract<Drag, { kind: 'resize' }>, dx: number, dy: number): void {
    const minX = imgX;
    const minY = imgY;
    const maxX = imgX + dispW();
    const maxY = imgY + dispH();
    const ratio = aspectLock();

    let left = d.fx;
    let top = d.fy;
    let right = d.fx + d.fw;
    let bottom = d.fy + d.fh;

    if (d.dir.includes('e')) right = clamp(d.fx + d.fw + dx, d.fx + MIN_MASK_SIZE, maxX);
    if (d.dir.includes('w')) left = clamp(d.fx + dx, minX, d.fx + d.fw - MIN_MASK_SIZE);
    if (d.dir.includes('s')) bottom = clamp(d.fy + d.fh + dy, d.fy + MIN_MASK_SIZE, maxY);
    if (d.dir.includes('n')) top = clamp(d.fy + dy, minY, d.fy + d.fh - MIN_MASK_SIZE);

    let w = right - left;
    let h = bottom - top;

    if (ratio != null) {
      // Lock aspect; drive by the dominant axis of this handle.
      const horizontal = d.dir.includes('e') || d.dir.includes('w');
      if (horizontal) {
        h = w / ratio;
      } else {
        w = h * ratio;
      }
      // Re-anchor based on which edges moved.
      if (d.dir.includes('w')) left = right - w; else right = left + w;
      if (d.dir.includes('n')) top = bottom - h; else bottom = top + h;
      // If aspect pushed us out of the image, scale down to fit.
      if (left < minX || right > maxX || top < minY || bottom > maxY) {
        const fitW = Math.min(w, maxX - minX);
        const fitH = Math.min(h, maxY - minY);
        const k = Math.min(fitW / w, fitH / h);
        w *= k; h *= k;
        if (d.dir.includes('w')) left = right - w; else right = left + w;
        if (d.dir.includes('n')) top = bottom - h; else bottom = top + h;
      }
    }

    fx = clamp(left, minX, maxX - MIN_MASK_SIZE);
    fy = clamp(top, minY, maxY - MIN_MASK_SIZE);
    fw = Math.max(MIN_MASK_SIZE, Math.min(w, maxX - fx));
    fh = Math.max(MIN_MASK_SIZE, Math.min(h, maxY - fy));
  }

  function onPointerUp(event: PointerEvent): void {
    touches.delete(event.pointerId);
    if (touches.size < 2) pinch = null;
    if (!drag) return;
    if (captureEl?.hasPointerCapture(event.pointerId)) {
      captureEl.releasePointerCapture(event.pointerId);
    }
    captureEl = null;
    drag = null;
  }

  /** Zoom the image around a stage point (frame center by default), keeping that point fixed. */
  function setZoom(value: number, anchorX = fx + fw / 2, anchorY = fy + fh / 2): void {
    const next = clamp(value, ZOOM_MIN, ZOOM_MAX);
    const sx = (anchorX - imgX) / dispW();
    const sy = (anchorY - imgY) / dispH();
    zoom = next;
    zoomSlider.value = String(next);
    imgX = anchorX - sx * dispW();
    imgY = anchorY - sy * dispH();
    clampImageToFrame();
    clampFrameToImage();
    scheduleRender();
  }

  zoomSlider.addEventListener('input', () => {
    if (ready) setZoom(Number(zoomSlider.value) || 1);
  });

  stage.addEventListener('wheel', (event) => {
    if (!ready) return;
    event.preventDefault();
    const box = stage.getBoundingClientRect();
    setZoom(zoom * Math.exp(-event.deltaY * WHEEL_ZOOM_SPEED), event.clientX - box.left, event.clientY - box.top);
  }, { passive: false });

  // --- Commit / cancel ----------------------------------------------------
  function computeCrop(): NormalizedCropRect | null {
    const dw = dispW();
    const dh = dispH();
    const x = clamp01((fx - imgX) / dw);
    const y = clamp01((fy - imgY) / dh);
    const width = clamp01(Math.min(fw / dw, 1 - x));
    const height = clamp01(Math.min(fh / dh, 1 - y));
    if (x <= 0.001 && y <= 0.001 && width >= 0.999 && height >= 0.999) return null;
    return { x, y, width, height };
  }

  function buildPatch(): Record<string, unknown> {
    const crop = computeCrop() ?? FULL_CROP;
    const ex = opts.item as Record<string, unknown>;
    const existing = (ex.imageTransform && typeof ex.imageTransform === 'object'
      ? { ...(ex.imageTransform as Record<string, unknown>) }
      : {}) as Record<string, unknown>;

    const box = croppedItemBox(
      {
        left: Number(opts.item.left) || 0,
        top: Number(opts.item.top) || 0,
        width: Number(opts.item.width) || 1,
        height: Number(opts.item.height) || 1,
      },
      opts.initialCrop ?? FULL_CROP,
      crop,
    );

    return {
      imageTransform: {
        ...existing,
        version: 1,
        // Always explicit (full rect when reset) so a legacy `imageCropper` never wins again.
        crop: { ...crop, scale: 1 },
        effects: existing.effects ?? { preset: 'none' },
      },
      // Legacy pixel crop state; `imageTransform` supersedes it everywhere.
      imageCropper: undefined,
      ...box,
    };
  }

  function commit(): void {
    if (!ready) return;
    opts.onCommit(buildPatch());
    destroy();
  }

  function cancel(): void {
    opts.onCancel();
    destroy();
  }

  doneBtn.addEventListener('click', (e) => { e.preventDefault(); commit(); });
  cancelBtn.addEventListener('click', (e) => { e.preventDefault(); cancel(); });
  resetBtn.addEventListener('click', (e) => {
    e.preventDefault();
    if (!ready) return;
    zoom = 1;
    zoomSlider.value = '1';
    activePreset = 'free';
    computeBaseFit();
    centerImage();
    fitFrameWithAspect(null);
    syncPresetButtons();
    renderPositions();
  });

  // Captured on window and stopped, so designer shortcuts (delete layer, deselect, undo…)
  // never act on the canvas behind the modal.
  function onKey(event: KeyboardEvent): void {
    event.stopPropagation();
    if (event.key === 'Escape') { event.preventDefault(); cancel(); return; }
    if (event.key === 'Enter') {
      const target = event.target as HTMLElement | null;
      if (target?.tagName === 'BUTTON' && overlay.contains(target)) return; // let the button click
      event.preventDefault();
      commit();
    }
  }

  // Clicking the dimmed backdrop (desktop) cancels.
  overlay.addEventListener('pointerdown', (event) => {
    if (event.target === overlay) cancel();
  });

  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);
  window.addEventListener('pointercancel', onPointerUp);
  window.addEventListener('keydown', onKey, true);

  function initFromImage(): void {
    if (destroyed) return;
    if (!natW || !natH || natW < 2 || natH < 2) {
      natW = Math.max(1, img.naturalWidth || natW || 1);
      natH = Math.max(1, img.naturalHeight || natH || 1);
    }
    status.remove();
    img.style.visibility = '';
    frame.style.visibility = '';
    doneBtn.disabled = false;
    doneBtn.style.opacity = '';
    computeBaseFit();
    zoom = 1;
    centerImage();
    if (opts.initialCrop && opts.initialCrop.width > 0 && opts.initialCrop.height > 0) {
      const c = opts.initialCrop;
      fx = imgX + c.x * dispW();
      fy = imgY + c.y * dispH();
      fw = c.width * dispW();
      fh = c.height * dispH();
      activePreset = 'free';
      clampFrameToImage();
    } else {
      activePreset = 'original';
      fitFrameWithAspect(null);
    }
    ready = true;
    syncPresetButtons();
    renderPositions();
  }

  /** Re-fit after the stage changes size (window resize, phone rotation), keeping the crop. */
  function relayout(): void {
    if (!ready) return;
    const before = stageRect();
    const oldW = dispW();
    const oldH = dispH();
    const rel = { x: (fx - imgX) / oldW, y: (fy - imgY) / oldH, w: fw / oldW, h: fh / oldH };
    const center = { x: imgX + oldW / 2 - before.w / 2, y: imgY + oldH / 2 - before.h / 2 };
    const oldBase = baseW;
    computeBaseFit();
    const k = baseW / Math.max(1e-6, oldBase);
    const now = stageRect();
    imgX = now.w / 2 + center.x * k - dispW() / 2;
    imgY = now.h / 2 + center.y * k - dispH() / 2;
    fx = imgX + rel.x * dispW();
    fy = imgY + rel.y * dispH();
    fw = rel.w * dispW();
    fh = rel.h * dispH();
    clampFrameToImage();
    clampImageToFrame();
    scheduleRender();
  }
  let lastStage = { w: 0, h: 0 };
  const resizeObserver = typeof ResizeObserver === 'function'
    ? new ResizeObserver(() => {
      const next = stageRect();
      if (next.w === lastStage.w && next.h === lastStage.h) return;
      const first = lastStage.w === 0;
      lastStage = next;
      if (!first) relayout();
    })
    : null;
  resizeObserver?.observe(stage);

  let destroyed = false;

  /** Load the first preview candidate that decodes and matches the source aspect. */
  async function loadPreview(): Promise<void> {
    const candidates = [...new Set(opts.sources.filter(Boolean))];
    for (const [index, src] of candidates.entries()) {
      img.src = src;
      try {
        await img.decode();
      } catch {
        continue;
      }
      if (destroyed) return;
      const last = index === candidates.length - 1;
      const known = !opts.vector && opts.sourceW > 1 && opts.sourceH > 1;
      if (!last && known && !sameAspect(img.naturalWidth, img.naturalHeight, opts.sourceW, opts.sourceH)) continue;
      // One frame so the stage has laid out and has dimensions.
      requestAnimationFrame(initFromImage);
      return;
    }
    if (destroyed) return;
    img.removeAttribute('src');
    status.textContent = t('This image could not be loaded.');
  }

  function destroy(): void {
    if (destroyed) return;
    destroyed = true;
    if (frameRequest) cancelAnimationFrame(frameRequest);
    resizeObserver?.disconnect();
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerUp);
    window.removeEventListener('keydown', onKey, true);
    overlay.remove();
    if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
  }

  document.body.appendChild(overlay);
  overlay.tabIndex = -1;
  overlay.focus({ preventScroll: true });
  void loadPreview();
  return destroy;
}

// --- small style helpers --------------------------------------------------

function cursorFor(dir: Direction): string {
  if (dir.length === 2) return `${dir}-resize`;
  return dir === 'n' || dir === 's' ? 'ns-resize' : 'ew-resize';
}

function chromeButton(label: string, variant: 'primary' | 'ghost'): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = label;
  Object.assign(btn.style, {
    minHeight: '40px',
    minWidth: '64px',
    padding: '0 16px',
    borderRadius: '12px',
    border: '0',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    color: variant === 'primary' ? '#fff' : 'var(--vs-ink, #fff)',
    background: variant === 'primary' ? 'var(--vs-accent, #007aff)' : 'var(--vs-surface-2, rgba(255,255,255,0.12))',
  } as CSSStyleDeclaration);
  return btn;
}

function presetButton(label: string): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = label;
  Object.assign(btn.style, {
    minHeight: '32px',
    padding: '0 12px',
    borderRadius: '999px',
    border: '1px solid var(--vs-line, rgba(255,255,255,0.16))',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    background: 'transparent',
    color: 'var(--vs-ink-2, rgba(255,255,255,0.75))',
  } as CSSStyleDeclaration);
  return btn;
}

function stylePresetButton(btn: HTMLButtonElement, active: boolean): void {
  btn.style.background = active ? 'var(--vs-accent, #007aff)' : 'transparent';
  btn.style.color = active ? '#fff' : 'var(--vs-ink-2, rgba(255,255,255,0.75))';
  btn.style.borderColor = active ? 'var(--vs-accent, #007aff)' : 'var(--vs-line, rgba(255,255,255,0.16))';
}

function fill(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce((text, [key, value]) => text.split(`{${key}}`).join(String(value)), template);
}

function dpiText(dpi: PrintDpi): string {
  const values = { dpi: Math.round(dpi.actual), target: Math.round(dpi.target) };
  const level = dpiLevel(dpi);
  if (level === 'ok') return fill(t('Print quality: {dpi} DPI'), values);
  if (level === 'warn') return fill(t('{dpi} DPI, below the {target} DPI target'), values);
  return fill(t('{dpi} DPI, may print blurry'), values);
}
